import React from 'react';
import Helmet from 'react-helmet';
import {
  type HreflangLink,
  getHrefLangCode,
} from '@repo/gatsby-localization-helpers';
import { sanitizeMetaText } from '@repo/utils/sanitizeMetaText';

type HelmetMeta = JSX.IntrinsicElements['meta'];
type HelmetLink = JSX.IntrinsicElements['link'];
type HelmetScript = JSX.IntrinsicElements['script'];

interface HeadProps {
  description?: string;
  lang?: string;
  meta?: HelmetMeta[];
  title?: string;
  keywords?: string;
  linkTags?: HelmetLink[];
  children?: React.ReactNode;
  scriptTags?: HelmetScript[];
  noIndex?: boolean;
  noFollow?: boolean;
  hreflangs?: HreflangLink[];
}

function Head({
  description = '',
  lang = 'en',
  meta = [],
  title = '',
  keywords = '',
  linkTags = [],
  children = null,
  scriptTags = [],
  noIndex = false,
  noFollow = false,
  hreflangs = [],
}: Readonly<HeadProps>) {
  const indexDirective = noIndex ? 'noindex' : 'index';
  const followDirective = noFollow ? 'nofollow' : 'follow';
  const robotsContent =
    process.env.GATSBY_ROBOTS_INDEX === 'true'
      ? `${indexDirective}, ${followDirective}`
      : 'noindex, nofollow';

  const seenHreflangCodes = new Set<string>();
  const dedupedHreflangs = hreflangs.filter(data => {
    const code = getHrefLangCode(data.lang);

    if (seenHreflangCodes.has(code)) return false;

    seenHreflangCodes.add(code);
    return true;
  });

  const hreflangLinks = dedupedHreflangs.map(data => (
    <link
      rel="alternate"
      hrefLang={getHrefLangCode(data.lang)}
      href={data.url}
      key={`hreflang-${data.lang}`}
    />
  ));

  const sanitizedTitle = sanitizeMetaText(title) ?? '';
  const sanitizedDescription = sanitizeMetaText(description);

  const helmetMeta: HelmetMeta[] = [
    {
      name: 'robots',
      content: robotsContent,
    },
    ...(sanitizedDescription
      ? [{ name: 'description', content: sanitizedDescription }]
      : []),
    {
      name: 'keywords',
      content: keywords,
    },
    ...meta,
  ];

  return (
    <Helmet
      htmlAttributes={{ lang: getHrefLangCode(lang) }}
      title={sanitizedTitle}
      meta={helmetMeta}
    >
      {linkTags.map(linkTag => linkTag)}
      {hreflangLinks.map(link => link)}
      {children}
      {scriptTags.map(scriptTag => scriptTag)}
    </Helmet>
  );
}

export default Head;
